import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft, Loader2 } from 'lucide-react';
import { ClinicPicker, Headline, PatientShell, useEnterClinic } from './PatientLogin';
import { SignedIn, loginErrorMessage, patientLoginApi } from '../../lib/patientLoginApi';
import { track } from '../../lib/analytics';

/**
 * Landing page for an emailed sign-in link (/patient/login/link#token=…).
 * The secret rides in the fragment so it never reaches a server log; it's
 * cleared from the address bar as soon as it's read.
 */
export function PatientLoginLink() {
  const enterClinic = useEnterClinic();
  const [signedIn, setSignedIn] = useState<SignedIn | null>(null);
  const [error, setError] = useState('');
  const started = useRef(false);

  useEffect(() => {
    // StrictMode runs effects twice in dev; the link is single-use.
    if (started.current) return;
    started.current = true;
    const token = new URLSearchParams(window.location.hash.slice(1)).get('token') || '';
    window.history.replaceState(null, '', window.location.pathname);
    // Only after the fragment is gone; analytics never reads it anyway.
    track('login_viewed', { entry: 'email_link' });
    patientLoginApi.verifyLink(token)
      .then(result => (result.clinics.length === 1 ? enterClinic(result.identityToken, result.clinics[0]) : setSignedIn(result)))
      .catch(err => setError(loginErrorMessage(err, 'This sign-in link is invalid or has expired.')));
  }, [enterClinic]);

  return <PatientShell>
    {signedIn ? <ClinicPicker signedIn={signedIn} /> : error ? (
      <div className="flex flex-col gap-6">
        <Headline sub={<span role="alert">{error} Links work once and expire after 15 minutes.</span>}>That link didn’t work</Headline>
        <Link to="/patient/login" className="inline-flex items-center gap-1.5 self-start text-[14px] font-medium text-[#6b5a50] hover:text-[#241a15]">
          <ArrowLeft className="h-4 w-4" /> Sign in another way
        </Link>
      </div>
    ) : (
      <p role="status" className="flex items-center gap-3 font-portal-display text-[22px] font-semibold"><Loader2 className="h-5 w-5 animate-spin text-[#e0563a]" /> Signing you in…</p>
    )}
  </PatientShell>;
}
